<template>
  <div
    :class="['ui-select', {
      opened: openedOnDesktop, loading, disabled, error, readonly,
    }]"
    @click="onClick"
  >
    <label v-if="label">
      {{ label }}
    </label>
    <div class="ui-select-input" @click="$emit('click-input')">
      <div
        v-if="currentColor"
        class="ui-select-option-color"
      >
        <div
          v-if="currentColor.type === 'code'"
          class="color-code"
          :style="{ backgroundColor: currentColor.color }"
        />
        <media
          v-else :src="currentColor.color"
          width="24" height="24"
          :alt="currentColor.name"
          not-lazy
        />
      </div>
      <input
        ref="input"
        type="text"
        autocomplete="off"
        readonly
        :placeholder="currentName || placeholder"
        :value="currentName"
      >
      <ui-loader v-if="loading" />
      <chevron-animated v-else class="icon" :reverse="openedOnDesktop" />
    </div>

    <div
      v-if="validationError"
      class="ui-select__error"
    >
      {{ validationError }}
    </div>

    <div :class="['ui-select-options', { labeled: label }]" @click.stop>
      <ui-dropdown
        :visible="openedOnDesktop"
        @on-close="onCloseDropdown"
      >
        <div
          v-for="option in computedOptions"
          :key="option.id"
          :class="{ chosen: option.value === modelValue }"
          @click="select(option)"
        >
          <div
            v-if="option.color"
            :class="['ui-select-option-color', { chosen: option.name === modelValue }]"
          >
            <div
              v-if="option.type === 'code'"
              class="color-code"
              :style="{ backgroundColor: option.color }"
            />
            <media
              v-else :src="option.color"
              width="24" height="24"
              :alt="option.name"
              not-lazy
            />
          </div>
          <span>{{ option.name }}</span>
          <f-sprite-icon
            v-if="!currentColor"
            :icon-link="$options.icons.IconCheck"
          />
        </div>
      </ui-dropdown>
    </div>

    <!-- Используется при работе с mobile/tablet для нативного отображения -->
    <client-only>
      <select
        v-if="!adaptive?.isDesktop && !currentColor && !custom"
        :value="chosenOption"
        class="ui-select-native"
        @change="onNativeChange"
        @input="onNativeInput"
        @click="onNativeClick"
        @focusout="onNativeBlur"
      >
        <option
          v-if="detectApple()"
          value=""
          selected
          disabled
        >
          {{ placeholder }}
        </option>
        <option
          v-for="(option, i) in computedOptions"
          :key="`$${i}-{option.value}`"
          :value="option.value"
          :selected="option.value === chosenOption"
        >
          {{ option.name }}
        </option>
      </select>
    </client-only>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject, PropType } from 'vue';

import validators from '@kit/ui-input/validators';

import ChevronAnimated from '@/assets/icons/chevron-animated.vue';
import {
  detectApple,
  detectWindows,
} from '@/utils/device/navigator-utils';
import { IAdaptive } from '@/utils/system/adaptive';

import IconCheck from '../../../assets/icons/check.svg';

export interface ISelectOptions {
  name: string;
  value: string|number;
  id?: string;
  type?: string;
  color?: string;
}

export default defineComponent({
  name: 'ui-select',

  components: {
    ChevronAnimated,
  },

  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },

    emptyValue: {
      type: String,
      default: '',
    },

    label: {
      type: String,
      default: '',
    },

    options: {
      type: Array as PropType<ISelectOptions[]>,
      default: () => [],
    },

    placeholder: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: () => false,
    },

    readonly: {
      type: Boolean,
      default: () => false,
    },

    loading: {
      type: Boolean,
      default: () => false,
    },

    validate: {
      type: String,
      default: '',
    },

    open: {
      type: Boolean,
      default: false,
    },

    custom: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'click-input',
    'click',
    'close',
    'change-value',
    'update:modelValue',
    'click-input',
    'click-input',
  ],

  setup() {
    const adaptive = inject<IAdaptive>('adaptive');

    return {
      adaptive,
    };
  },

  data() {
    return {
      isOpened: this.open,
      isNativeSelected: false,
      validationError: '',
    };
  },

  icons: {
    IconCheck,
  },

  computed: {
    chosenOption: {
      get(): string|number {
        return this.modelValue;
      },

      set(option: string|number) {
        this.select(option);
      },
    },

    currentName(): string {
      // если v-model у нас один из входящих value
      return this.options.find(({ value }) => value === this.modelValue)?.name || `${this.modelValue}`;
    },

    currentColor() {
      return this.options.find(({ name, type, color }) => type && color && name === this.modelValue);
    },

    computedOptions(): ISelectOptions[] {
      // у некоторых опшинов нет value, приводим к одному виду
      const options = this.options.map(({ name, value, ...rest }) => ({
        name,
        value: value || name,
        ...rest,
      }));
      const empty = { name: this.emptyValue, value: '' };

      return this.emptyValue ? [empty, ...options] : options;
    },

    openedOnDesktop() { // кроме цветов(из-за визуала везде в одном виде)
      return this.isOpened && (this.adaptive?.isDesktop || !!this.currentColor || this.custom);
    },

    error() {
      return !!this.validationError;
    },
  },

  watch: {
    open(value) {
      this.isOpened = value;
    },
  },

  methods: {
    onClick() {
      if (this.loading) return;

      this.isOpened = !this.isOpened;
      this.$emit('click', this.isOpened);
      this.validationError = '';
    },
    onCloseDropdown() {
      this.isOpened = false;
      this.$emit('close');
    },
    select(option?: ISelectOptions) {
      if (!option) return;
      if (option?.name !== this.modelValue) {
        this.$emit('change-value', option);
      }
      this.$emit('update:modelValue', option?.value);
      // для десктопа явно закрываем дропдаун
      this.isOpened = false;
    },
    onNativeChange(e: InputEvent) {
      const targetValue = (e.target as HTMLOptionElement).value;
      const selectedOption = this.computedOptions.find(({ value }) => String(value) === String(targetValue));

      this.select(selectedOption);
    },
    onNativeInput() {
      if (!detectWindows()) return; // не реагируем на инпут везде кроме винды
      this.isNativeSelected = true;
    },
    onNativeClick() {
      if (this.isNativeSelected) { // не реагируем на клик внутри селекта
        this.isNativeSelected = false;
        return;
      }
      this.$emit('click-input');
    },
    onNativeBlur() {
      // не на винде имитируем второе событие клика по blur
      if (!detectWindows()) this.$emit('click-input');
    },

    checkValid() {
      if (!this.validate.length) {
        return false;
      }

      const [validation] = validators[this.validate];

      if (typeof validation === 'function') {
        return validation(`${this.modelValue}`);
      }
      const [regex, err] = validation;
      if (!(new RegExp(regex)).test(String(this.modelValue))) {
        return err;
      }
    },

    validateInput() {
      const validation = this.checkValid();

      if (typeof validation === 'string') {
        this.validationError = validation;
      } else {
        this.validationError = '';
      }

      return this.validationError;
    },

    checkValidOutside() {
      return !this.validateInput();
    },
    detectApple,
  },
});
</script>

<style lang="scss">
.ui-select {
  position: relative;
  cursor: pointer;

  label {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    line-height: 20px;
    color: $gray;
  }

  &-input {
    position: relative;
    display: flex;
    height: 40px;
    padding: 9px 12px;
    border: 1px solid $gray_lu;
    border-radius: 4px;
    overflow: hidden;
    transition: .2s ease border-color;
    align-items: center;
    background: $white;
    color: $gray;

    input {
      width: calc(100% - 20px);
      padding: 0;
      font-size: 14px;
      line-height: 20px;
      color: $black;
      border: none;
      outline: none;
      box-shadow: 0 0 0 1000px $white inset;
      cursor: pointer;
      /* stylelint-disable-next-line */
      &::-webkit-input-placeholder {
        color: $gray;
        line-height: 20px;
        transition: .2s color ease;

        &:focus {
          color: transparent;
        }
      }
    }

    .icon {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 16px;
      height: 16px;
      color: $gray_lu;
    }
  }

  &.loading {
    pointer-events: none;

    input {
      display: none;
    }
  }

  &-native {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    opacity: 0;
  }

  &.readonly,
  &.disabled {
    pointer-events: none;

    .icon {
      display: none;
    }
  }

  &.disabled {
    input,
    .ui-select-input {
      background-color: $gray_bgr;
      box-shadow: 0 0 0 1000px $gray_bgr inset;
    }
  }

  &.readonly {
    input,
    .ui-select-input {
      background-color: $white;
      box-shadow: 0 0 0 1000px $white inset;
    }
  }

  &-options {
    position: absolute;
    top: 48px;
    width: 100%;

    &.labeled {
      top: 72px;
    }

    .ui-dropdown,
    .ui-dropdown-content {
      width: 100%;
    }

    .ui-dropdown-content {
      border-radius: 4px;
    }

    .ui-dropdown-scrollview {
      max-height: 236px;
      padding: 8px 0;

      & > div:not(.ps__rail-x, .ps__rail-y) {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: 8px 12px;
        font-size: 14px;
        line-height: 24px;
        background-color: #fff;
        cursor: pointer;
        transition: .2s background-color $gray_bgr;

        &:hover {
          background-color: $gray_bgr;
        }

        & > span {
          width: calc(100% - 16px);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        & > svg {
          width: 16px;
          height: 16px;
          color: $gray_l;
          opacity: 0;
          transition: .2s opacity ease;
        }

        &.chosen > svg {
          opacity: 1;
        }
      }
    }
  }

  &.opened {
    .ui-select-input {
      border-color: $black;
    }
  }

  &-option-color {
    position: relative;
    padding: 4px;
    margin: 0 8px 0 0;
    border-radius: 50%;
    cursor: pointer;
    transition: .2s box-shadow;

    &:hover {
      background-color: $gray_bgr;
    }

    &.chosen {
      box-shadow: inset 0 0 0 1px $black;
    }

    & + input {
      width: calc(100% - 52px);
    }

    .color-code,
    img {
      display: block;
      width: 16px;
      height: 16px;
      border-radius: 50%;
    }
  }

  &__error {
    font-size: 12px;
    line-height: 20px;
    margin-top: 4px;
    color: $red;
  }

  &.error:not(.disabled) .ui-select-input {
    border-color: $red !important;
  }
}
</style>
